import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Users, Plus, Building2, Link2, Copy, Eye, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, DollarSign, TrendingUp, ClipboardList
} from 'lucide-react';

const AffiliateManagement = () => {
  const [tab, setTab] = useState('affiliates');
  const [affiliates, setAffiliates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterAffiliate, setFilterAffiliate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [affRes, statsRes] = await Promise.all([
        axios.get(`${API}/affiliates`),
        axios.get(`${API}/affiliates/stats`)
      ]);
      setAffiliates(affRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load affiliate data');
    } finally { setLoading(false); }
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      let url = `${API}/affiliates/all-bookings`;
      const params = [];
      if (filterAffiliate) params.push(`affiliate_id=${filterAffiliate}`);
      if (filterStatus) params.push(`status=${filterStatus}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url);
      setBookings(res.data);
    } catch { toast.error('Failed to load bookings'); }
  }, [filterAffiliate, filterStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === 'bookings') loadBookings(); }, [tab, loadBookings]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/affiliates/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status}`);
      loadBookings();
      loadAll();
    } catch { toast.error('Failed to update status'); }
  };

  const deleteAffiliate = async (id) => {
    if (!window.confirm('Delete this affiliate and all their bookings?')) return;
    try {
      await axios.delete(`${API}/affiliates/${id}`);
      toast.success('Affiliate deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (aff) => {
    try {
      await axios.put(`${API}/affiliates/${aff.id}`, { is_active: !aff.is_active });
      toast.success(aff.is_active ? 'Deactivated' : 'Activated');
      loadAll();
    } catch { toast.error('Failed to update'); }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    toast.success('Public link copied!');
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  if (loading) return <Layout><div className="text-center py-12">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="affiliate-management">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-2xl font-bold text-gray-900">Affiliate Management</h2>
          <button onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition"
            data-testid="create-affiliate-btn">
            <Plus className="w-4 h-4" /> New Affiliate
          </button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-blue-600" /><span className="text-xs text-gray-500">Affiliates</span></div>
              <div className="text-xl font-bold">{stats.total_affiliates}</div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-2 mb-1"><ClipboardList className="w-4 h-4 text-purple-600" /><span className="text-xs text-gray-500">Total Bookings</span></div>
              <div className="text-xl font-bold">{stats.total_bookings}</div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-emerald-600" /><span className="text-xs text-gray-500">Total Sales</span></div>
              <div className="text-xl font-bold">{stats.total_sales?.toFixed(2)} EUR</div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-amber-600" /><span className="text-xs text-gray-500">Total Profit</span></div>
              <div className="text-xl font-bold">{stats.total_profit?.toFixed(2)} EUR</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {['affiliates', 'bookings'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t === 'affiliates' ? 'Affiliates' : 'All Bookings'}
            </button>
          ))}
        </div>

        {/* Affiliates Tab */}
        {tab === 'affiliates' && (
          <div className="space-y-3">
            {affiliates.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No affiliates yet. Create one to get started.</p>
            ) : affiliates.map(aff => (
              <div key={aff.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`affiliate-card-${aff.id}`}>
                <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer"
                  onClick={() => setExpandedAffiliate(expandedAffiliate === aff.id ? null : aff.id)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${aff.is_active ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      <Building2 className={`w-5 h-5 ${aff.is_active ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{aff.company_name}</div>
                      <div className="text-xs text-gray-500">{aff.contact_name} - {aff.email}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${aff.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {aff.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{aff.type}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{aff.booking_count} bookings</span>
                    <span className="font-medium text-emerald-600">{aff.total_revenue?.toFixed(2)} EUR</span>
                    <button onClick={(e) => { e.stopPropagation(); copyLink(aff.slug); }} className="p-1 hover:bg-gray-100 rounded" title="Copy public link">
                      <Copy className="w-4 h-4" />
                    </button>
                    {expandedAffiliate === aff.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {expandedAffiliate === aff.id && (
                  <div className="border-t p-4 bg-gray-50 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-gray-500">Phone:</span> {aff.phone || '-'}</div>
                      <div><span className="text-gray-500">Slug:</span> {aff.slug}</div>
                      <div><span className="text-gray-500">Commission:</span> {aff.commission_rate}%</div>
                      <div>
                        <span className="text-gray-500">Public Link:</span>{' '}
                        <span className="text-emerald-600 text-xs">/book/{aff.slug}</span>
                      </div>
                    </div>
                    {aff.notes && <div className="text-sm text-gray-600"><span className="text-gray-500">Notes:</span> {aff.notes}</div>}
                    <div className="flex gap-2">
                      <button onClick={() => toggleActive(aff)}
                        className={`px-3 py-1 text-xs rounded-lg ${aff.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                        {aff.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => deleteAffiliate(aff.id)}
                        className="px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bookings Tab */}
        {tab === 'bookings' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <select value={filterAffiliate} onChange={e => setFilterAffiliate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="filter-affiliate">
                <option value="">All Affiliates</option>
                {affiliates.map(a => <option key={a.id} value={a.id}>{a.company_name}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="filter-status">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {bookings.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No bookings found.</p>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 text-left text-gray-500 text-xs uppercase">
                      <th className="px-4 py-3">Date</th><th className="px-4 py-3">Affiliate</th>
                      <th className="px-4 py-3">Activity</th><th className="px-4 py-3">Guest</th>
                      <th className="px-4 py-3">Guests</th><th className="px-4 py-3">B2B</th>
                      <th className="px-4 py-3">Selling</th><th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
                    </tr></thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b.id} className="border-t hover:bg-gray-50" data-testid={`admin-booking-${b.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap">{b.booking_date}{b.booking_time ? ` ${b.booking_time}` : ''}</td>
                          <td className="px-4 py-3">{b.affiliate_name}</td>
                          <td className="px-4 py-3 font-medium">{b.activity_name}</td>
                          <td className="px-4 py-3">{b.guest_name}<br/><span className="text-xs text-gray-400">{b.guest_phone}</span></td>
                          <td className="px-4 py-3">{b.num_guests}</td>
                          <td className="px-4 py-3 text-gray-500">{b.b2b_price} {b.b2b_currency}</td>
                          <td className="px-4 py-3 font-medium text-emerald-600">{b.selling_price} {b.selling_currency}</td>
                          <td className="px-4 py-3"><span className={`text-xs ${b.source === 'public_link' ? 'text-blue-600' : 'text-gray-500'}`}>{b.source}</span></td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[b.status] || ''}`}>{b.status}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {b.status === 'pending' && (
                                <>
                                  <button onClick={() => updateBookingStatus(b.id, 'confirmed')} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Confirm">
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => updateBookingStatus(b.id, 'cancelled')} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Cancel">
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              {b.status === 'confirmed' && (
                                <button onClick={() => updateBookingStatus(b.id, 'completed')}
                                  className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100">Complete</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Affiliate Modal */}
        {showCreateModal && <CreateAffiliateModal onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); loadAll(); }} />}
      </div>
    </Layout>
  );
};

const CreateAffiliateModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    slug: '', company_name: '', contact_name: '', email: '', phone: '',
    type: 'agent', password: '', commission_rate: 0, notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/affiliates`, form);
      toast.success('Affiliate created!');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create affiliate');
    } finally { setSubmitting(false); }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-affiliate-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Create New Affiliate</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input required value={form.company_name}
                onChange={e => { setForm(f => ({...f, company_name: e.target.value, slug: f.slug || generateSlug(e.target.value)})); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="aff-company-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
              <input required value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="aff-contact-name" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL) * <span className="text-xs text-gray-400">/book/{form.slug || '...'}</span></label>
            <input required value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="john-travel" data-testid="aff-slug" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="aff-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="aff-type">
                <option value="agent">Agent</option><option value="hostel">Hostel</option>
                <option value="hotel">Hotel</option><option value="guide">Guide</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission %</label>
              <input type="number" min="0" max="100" step="0.1" value={form.commission_rate}
                onChange={e => setForm(f => ({...f, commission_rate: parseFloat(e.target.value) || 0}))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input type="password" required value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" data-testid="aff-password" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={2} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="submit-create-affiliate">
              {submitting ? 'Creating...' : 'Create Affiliate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateManagement;
